import {alpha, Box, CircularProgress, Stack, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import {Line} from 'react-chartjs-2';
import {useObservable} from '@ngneat/react-rxjs';

import {StatisticsMode, statisticsQuery} from '@store/statistics';

import {Colors} from '@constants/colors.constants';
import {NumberExt} from '@utils/extensions/number.extension';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const TurnoverAndMarginChart = () => {
  const {t} = useTranslation();

  const [filters] = useObservable(statisticsQuery.filters$);

  const [{turnoverAndPurchase, loading}] = useObservable(statisticsQuery.turnoverAndPurchase$);

  return (
    <Stack flex={1} height="360px" bgcolor={alpha(Colors.primary, 0.2)} borderRadius="4px" px="3px" py="20px">
      <Typography fontSize={18} fontWeight="700" ml="17px" mb="20px">
        {t('dashboard.statistics.finance.turnoverAndMargin.title')}
      </Typography>
      {loading && <CircularProgress size={30} style={{alignSelf: 'center'}}/>}
      {!loading && (
        <Box flex={1} mb="15px" width="100%">
          <Line
            height="100%"
            options={{
              plugins: {
                legend: {display: false},
                title: {display: false},
                datalabels: {display: false},
                tooltip: {
                  backgroundColor: Colors.background,
                  displayColors: true,
                  cornerRadius: 4,
                  padding: 5,
                  xAlign: 'left',
                  yAlign: 'bottom',
                  caretSize: 0,
                  caretPadding: 10,
                  bodyColor: Colors.text,
                  bodyFont: {
                    family: 'CircularStd, sans-serif',
                    size: 14,
                    weight: '500',
                  },
                  callbacks: {
                    title: function () {
                      return '';
                    },
                    label: function (context) {
                      let label = context.dataset.label || '';
                      if (label) {
                        label += ': ';
                      }
                      if (context.parsed.y !== null) {
                        label += NumberExt.toCurrency(context.parsed.y);
                      }
                      return label;
                    },
                  },
                },
              },
              maintainAspectRatio: false,
              interaction: {
                mode: 'index',
                intersect: false,
              },
              scales: {
                x: {
                  grid: {
                    display: false,
                    drawBorder: false,
                    drawTicks: false,
                  },
                  ticks: {
                    color: Colors.text,
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 10,
                    },
                    backdropPadding: 0,
                    padding: 10,
                  },
                },
                y: {
                  beginAtZero: true,
                  grid: {
                    borderDash: [2],
                    color: alpha(Colors.chartBorder, 0.5),
                    drawBorder: false,
                    drawTicks: false,
                  },
                  ticks: {
                    count: 6,
                    color: Colors.text,
                    font: {
                      family: 'CircularStd, sans-serif',
                      size: 10,
                    },
                    backdropPadding: 0,
                    padding: 10,
                  },
                },
              },
            }}
            data={{
              labels: StatisticsMode.labels(filters),
              datasets: [
                {
                  label: t('dashboard.statistics.finance.turnoverAndMargin.sellPrediction'),
                  data: turnoverAndPurchase?.map((item) => item.turnoverExecLines) ?? [],
                  borderColor: Colors.blue,
                  backgroundColor: Colors.blue,
                  cubicInterpolationMode: 'monotone',
                  pointRadius: 0,
                  pointHoverBorderColor: Colors.background,
                  pointHoverBackgroundColor: Colors.blue,
                  pointHoverRadius: 5,
                  pointHoverBorderWidth: 4,
                  borderDash: [5, 5],
                },
                {
                  label: t('dashboard.statistics.finance.turnoverAndMargin.buyPrediction'),
                  data: turnoverAndPurchase?.map((item) => item.purchaseExecLines) ?? [],
                  borderColor: Colors.warning,
                  backgroundColor: Colors.warning,
                  cubicInterpolationMode: 'monotone',
                  pointRadius: 0,
                  pointHoverBorderColor: Colors.background,
                  pointHoverBackgroundColor: Colors.warning,
                  pointHoverRadius: 5,
                  pointHoverBorderWidth: 4,
                  borderDash: [5, 5],
                },
                {
                  label: t('dashboard.statistics.finance.turnoverAndMargin.sellInvoicing'),
                  data: turnoverAndPurchase?.map((item) => item.turnoverInvoiceLines) ?? [],
                  borderColor: Colors.blue,
                  backgroundColor: Colors.blue,
                  cubicInterpolationMode: 'monotone',
                  pointRadius: 0,
                  pointHoverBorderColor: Colors.background,
                  pointHoverBackgroundColor: Colors.blue,
                  pointHoverRadius: 5,
                  pointHoverBorderWidth: 4,
                },
                {
                  label: t('dashboard.statistics.finance.turnoverAndMargin.buyInvoicing'),
                  data: turnoverAndPurchase?.map((item) => item.purchaseInvoiceLines) ?? [],
                  borderColor: Colors.warning,
                  backgroundColor: Colors.warning,
                  cubicInterpolationMode: 'monotone',
                  pointRadius: 0,
                  pointHoverBorderColor: Colors.background,
                  pointHoverBackgroundColor: Colors.warning,
                  pointHoverRadius: 5,
                  pointHoverBorderWidth: 4,
                },
              ],
            }}
          />
        </Box>
      )}
      <Stack
        bgcolor={Colors.background}
        borderRadius="4px"
        px="20px"
        py="10px"
        direction="row"
        alignItems="center"
        alignSelf="center"
        spacing={3}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" border="dotted" borderColor={Colors.warning}/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.purchaseExecLines')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.warning} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.purchaseInvoiceLines')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" border="dotted" borderColor={Colors.blue}/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.turnoverExecLines')}
          </Typography>
        </Stack>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box height="11px" width="11px" bgcolor={Colors.blue} borderRadius="3px"/>
          <Typography fontSize={12} fontWeight="400">
            {t('dashboard.statistics.finance.turnoverAndMargin.turnoverInvoiceLines')}
          </Typography>
        </Stack>

      </Stack>
    </Stack>
  );
};

export default TurnoverAndMarginChart;
